import { htmlToPlainText } from '../components/AssessmentBuilder/ScreenBuilder/utils';

const VALID_OPTION_TEXT_ALIGNMENTS = new Set(['left', 'center', 'right']);

// `boolean` was historically persisted in responseConfig.layout as a yes/no
// marker, but it is NOT a real visual layout — a yes/no question is simply a
// vertical single-select whose identity lives on the question's data.type.
// Storing it in `layout` meant yes/no fell outside the recognized options-list
// layouts ('vertical' | 'horizontal' | 'grid'), which hid the Option Appearance
// menu and broke alignment/direction. Resolve it here so every layout-driven
// control and renderer treats yes/no exactly like ordinal/nominal. Any other
// value (including undefined) passes through unchanged.
export const resolveOptionsLayout = (layout) => (layout === 'boolean' ? 'vertical' : layout);

export const getOptionBoxWidth = (responseConfig = {}, scale = 1) => {
  const width = Number(responseConfig.optionBoxWidth);
  if (!Number.isFinite(width) || width <= 0) {
    return undefined;
  }

  return `${width * scale}px`;
};

export const getOptionTextAlign = (responseConfig = {}, fallback = 'center') => {
  const textAlign = responseConfig.optionTextAlign;
  return VALID_OPTION_TEXT_ALIGNMENTS.has(textAlign) ? textAlign : fallback;
};

export const estimateOptionBoxWidth = (content = '', responseConfig = {}) => {
  const fontSize = Number(responseConfig.optionFontSize) || 14;
  const padding = Number(responseConfig.optionPadding) || 8;
  const text = htmlToPlainText(content).replace(/\s+/g, '');

  return Math.ceil(text.length * fontSize * 0.5 + padding * 4 - 2);
};

export const getFallbackMeasuredOptionBoxWidth = (content, responseConfig = {}, measuredWidth) => {
  const configuredWidth = getOptionBoxWidth(responseConfig);
  if (configuredWidth) {
    return configuredWidth;
  }

  const measured = Number(measuredWidth);
  if (!Number.isFinite(measured) || measured <= 0) {
    return undefined;
  }

  const estimatedWidth = estimateOptionBoxWidth(content, responseConfig);
  return `${Math.max(measured, estimatedWidth)}px`;
};

export const getOptionButtonColors = (responseConfig = {}, isSelected = false) => {
  if (isSelected) {
    return {
      backgroundColor: responseConfig.btnSelectedBgColor || '#0d9488',
      color: responseConfig.btnSelectedTextColor || '#ffffff',
      borderColor: responseConfig.btnSelectedBorderColor || '#0d9488',
    };
  }

  const isFilled = responseConfig.buttonStyle === 'filled' || responseConfig.buttonStyle === 'solid';

  return {
    backgroundColor: responseConfig.btnBgColor || (isFilled ? '#0d9488' : '#ffffff'),
    color: responseConfig.btnTextColor || (isFilled ? '#ffffff' : '#0d9488'),
    borderColor: responseConfig.btnBorderColor || '#0d9488',
  };
};

export const getOptionBoxHeight = (responseConfig = {}, scale = 1) => {
  const height = Number(responseConfig.optionBoxHeight);
  if (!Number.isFinite(height) || height <= 0) {
    return undefined;
  }

  return `${height * scale}px`;
};

export const getOptionSpacing = (responseConfig = {}, fallback = 8) => {
  const raw = Number(responseConfig.spacing);
  if (!Number.isFinite(raw) || raw < 0) {
    return fallback;
  }
  return raw;
};

// Single source of truth for how a single-select option group is laid out, so
// the builder canvas and the survey preview/participant render IDENTICALLY.
//
//   • vertical   → inline-level column that HUGS its content (fit-content), so
//                  the parent's textAlign (optionsAlignment) actually moves the
//                  whole group left/center/right. `equalWidth` makes the BUTTONS
//                  fill that column (so they match each other) without forcing
//                  the group to span the full row — radios always hug content.
//   • horizontal → block-level, full-width wrapping flex ROW positioned by
//                  justifyContent. Buttons keep their natural width here: a
//                  width:100% button in a row would wrap onto its own line and
//                  the group would look vertical (the exact yes/no bug).
//   • grid       → full-width CSS grid; buttons fill their cell.
export const getOptionsGroupLayout = (responseConfig = {}) => {
  const layout = resolveOptionsLayout(responseConfig.layout) || 'vertical';
  const isGrid = layout === 'grid';
  const isHorizontal = layout === 'horizontal';
  const isVertical = !isGrid && !isHorizontal;
  const isButton = ['outline', 'filled', 'solid'].includes(responseConfig.buttonStyle);
  const alignment = responseConfig.optionsAlignment || 'left';
  const justifyContent = alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start';
  const columns = responseConfig.columns || responseConfig.gridColumns || 2;
  const spacing = getOptionSpacing(responseConfig);

  const className = isGrid ? 'grid' : isHorizontal ? 'flex flex-wrap' : 'flex-col items-start';
  const style = {
    gap: `${spacing}px`,
    gridTemplateColumns: isGrid ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
    display: isVertical ? 'inline-flex' : undefined,
    width: isVertical ? 'fit-content' : '100%',
    justifyContent: isVertical ? undefined : justifyContent,
  };

  // Buttons fill their slot (equal width) only for vertical & grid; never in a
  // horizontal row, where width:100% would break the row into a stack.
  const buttonFillsWidth = !!responseConfig.equalWidth && isButton && !isHorizontal;

  return { layout, isGrid, isHorizontal, isVertical, className, style, buttonFillsWidth };
};

export const getOptionButtonSizingStyle = (responseConfig = {}, options = {}) => {
  const { scale = 1, fallbackTextAlign = 'center' } = options;
  const width = getOptionBoxWidth(responseConfig, scale);
  const height = getOptionBoxHeight(responseConfig, scale);
  const textAlign = getOptionTextAlign(responseConfig, fallbackTextAlign);

  // When an explicit height is set, switch the button to inline-flex so the
  // label is vertically centered inside the taller box. Default (no height)
  // keeps the original inline-block behavior so existing buttons are unaffected.
  const flexJustify = textAlign === 'left' ? 'flex-start'
    : textAlign === 'right' ? 'flex-end'
    : 'center';

  return {
    ...(width ? { width, minWidth: width } : {}),
    ...(height ? {
      height,
      minHeight: height,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: flexJustify,
    } : {}),
    textAlign,
  };
};
